import styled from 'styled-components';
import { useField } from 'react-final-form';
import { NotesInputField } from './NotesInputField';
import { AiPreview } from './AiPreview';
import { OriginalText } from './OriginalText';
import { UseAiTextButton } from './UseAiTextButton';
import { useAiCommentAction } from 'hooks';
import { useVehicleEditing } from 'context/isAnotherUserEditingContext';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

type CreateFieldKeyFN = (sectionId: string, fieldName?: string) => string;

type Props = {
  sectionId: string;
  onSubmit?: () => void;
  skipSave?: boolean;
  aiNotes?: boolean;
  autoFocus?: boolean;
  save?: (message: string | null, field?: string) => Promise<void> | void;
  getFieldKey?: CreateFieldKeyFN;
};

const defaultCreateFieldKey: CreateFieldKeyFN = (sectionId, fieldName) =>
  [sectionId, 'notes', fieldName].filter(Boolean).join('.');

export const ReusableNotesField = ({
  save,
  sectionId,
  aiNotes = false,
  onSubmit = () => {},
  skipSave = false,
  getFieldKey = defaultCreateFieldKey
}: Props) => {
  const { input: notesInput } = useField(getFieldKey(sectionId, 'message'), { subscription: { value: true } });
  const {
    input: { value: originalMessage }
  } = useField(getFieldKey(sectionId, 'original_message'), { subscription: { value: true } });
  const { input: statusInput } = useField(getFieldKey(sectionId, 'status'), { subscription: { value: true } });
  // required for proper re-rendering
  useField(getFieldKey(sectionId, 'ai_message'), { subscription: { value: true } });

  const { status, action, buttonColor, buttonLabel } = useAiCommentAction(getFieldKey(sectionId), save);
  const aiSelected = statusInput.value === 'ai_used';

  const setNotes = (notes: string) => {
    notesInput.onChange(notes);
    onSubmit();
    if (!skipSave && !!save) {
      save(notes);
    }
  };

  if (aiSelected) {
    return (
      <Container>
        {aiNotes && <OriginalText message={originalMessage} />}
        <NotesInputField autoFocus={false} initialValue={notesInput.value} icon="gpt" save={setNotes} />
      </Container>
    );
  }

  return (
    <Container>
      <NotesInputField autoFocus={!aiNotes} initialValue={notesInput.value} save={setNotes} />
      {aiNotes && (
        <>
          <UseAiTextButton text={buttonLabel} bg={buttonColor} handleClick={action} />
          {status === 'ai_fetched' && save && <AiPreview save={save!} fieldId={getFieldKey(sectionId, 'ai_message')} />}
        </>
      )}
    </Container>
  );
};
